/**
 * wrapperDisciplineEnforcer.js — WRAPPER-DISCIPLINE-EXT-1
 *
 * Runtime and CI enforcement for triple-wrapper governance.
 *
 * Two scan passes:
 *   1. Direct Anthropic SDK imports outside the 3 Tier A governed dirs
 *   2. Files matching `governed*Call` pattern NOT registered in WRAPPER_REGISTRY
 *      (the "unregistered governed*Call" detector — only unregistered files are
 *       flagged; registered Tier B routing shims are exempt)
 *
 * Operating modes:
 *   strict: true  → throw on violations (CI default)
 *   strict: false → warn and return violations (runtime default)
 *
 * Node.js context guard: fs/path operations are guarded. In browser/non-Node
 * environments the enforcer skips the filesystem scan and returns a registry-
 * only structural check. Full enforcement runs in CI via check-wrapper-discipline.mjs.
 *
 * Cross-wrapper usage (in governedAegisCall, governedArtemisCall, governedCouncilCall):
 *   import { checkRegistryIntegrity } from '../governance/wrapperDisciplineEnforcer.js';
 *   // runs registry-only check safe for browser context
 *
 * Wired into:
 *   - scripts/check-wrapper-discipline.mjs (CI / pre-commit, Node.js)
 *   - package.json prebuild (Node.js)
 *   - .github/workflows/wrapper-discipline.yml
 *   - All 3 Tier A wrappers (registry-integrity check at module load)
 */

import {
  WRAPPER_REGISTRY,
  TIER_A_WRAPPERS,
  ALL_REGISTERED_PATHS,
} from './wrapperRegistry.js';

// ─── Environment guard ────────────────────────────────────────────────────────

const IS_NODE =
  typeof process !== 'undefined' &&
  process.versions != null &&
  process.versions.node != null;

// ─── Registry integrity check (browser-safe) ─────────────────────────────────

/**
 * Verify that the WRAPPER_REGISTRY contains exactly 3 Tier A SDK wrappers
 * and that all required registry fields are present.
 * This check runs in any environment (browser or Node.js).
 *
 * @returns {{ violations: Array<object>, passed: boolean }}
 */
export function checkRegistryIntegrity() {
  const violations = [];
  const tierAEntries = Object.entries(WRAPPER_REGISTRY).filter(
    ([, v]) => v.tier === 'sdk-wrapper'
  );

  // Exactly 3 Tier A wrappers required
  if (tierAEntries.length !== 3) {
    violations.push({
      type: 'tier_a_count_mismatch',
      expected: 3,
      actual: tierAEntries.length,
      message: `Registry must contain exactly 3 Tier A SDK wrappers; found ${tierAEntries.length}`,
    });
  }

  // Required canonical Tier A names
  const requiredTierA = ['governedAegisCall', 'governedArtemisCall', 'governedCouncilCall'];
  for (const name of requiredTierA) {
    if (!WRAPPER_REGISTRY[name]) {
      violations.push({
        type: 'tier_a_missing',
        wrapper: name,
        message: `Required Tier A SDK wrapper "${name}" not found in WRAPPER_REGISTRY`,
      });
    } else if (WRAPPER_REGISTRY[name].tier !== 'sdk-wrapper') {
      violations.push({
        type: 'tier_a_misclassified',
        wrapper: name,
        actualTier: WRAPPER_REGISTRY[name].tier,
        message: `"${name}" must have tier: 'sdk-wrapper'; found: '${WRAPPER_REGISTRY[name].tier}'`,
      });
    }
  }

  // Each entry must have required fields
  const requiredFields = ['tier', 'directSdk', 'path', 'purpose'];
  for (const [name, config] of Object.entries(WRAPPER_REGISTRY)) {
    for (const field of requiredFields) {
      if (config[field] == null) {
        violations.push({
          type: 'registry_entry_incomplete',
          wrapper: name,
          missingField: field,
          message: `Registry entry "${name}" is missing required field: ${field}`,
        });
      }
    }
    // Tier A must have directSdk: true
    if (config.tier === 'sdk-wrapper' && config.directSdk !== true) {
      violations.push({
        type: 'tier_a_directsdk_false',
        wrapper: name,
        message: `Tier A entry "${name}" must have directSdk: true`,
      });
    }
    // Tier B must have directSdk: false
    if (config.tier === 'routing-shim' && config.directSdk !== false) {
      violations.push({
        type: 'tier_b_directsdk_true',
        wrapper: name,
        message: `Tier B entry "${name}" must have directSdk: false`,
      });
    }
  }

  return { violations, passed: violations.length === 0 };
}

// ─── Node.js-only filesystem scans ───────────────────────────────────────────

/**
 * Scan `rootDir` recursively for files that import the Anthropic SDK directly
 * (`@anthropic-ai/sdk`), excluding:
 *   - Tier A wrapper directories (they ARE allowed to import the SDK)
 *   - Test fixtures, test files, and mock files (intentional violations exist
 *     in src/services/testAdversarial/__fixtures__/ for adversarial testing)
 *
 * @param {string} rootDir   - root directory to scan (relative to cwd)
 * @param {string[]} allowedDirs - directories where Anthropic imports are permitted
 * @returns {Array<{ type: string, file: string, line: string }>}
 */
function scanDirectAnthropicImports(rootDir, allowedDirs) {
  if (!IS_NODE) return [];
  const { execSync } = await_require_sync('node:child_process');
  if (!execSync) return [];

  const violations = [];
  const ANTHROPIC_PATTERN = `from ['"]@anthropic-ai`;

  let grepOutput = '';
  try {
    grepOutput = execSync(
      `grep -rn "${ANTHROPIC_PATTERN}" "${rootDir}" --include="*.js" --include="*.jsx" --include="*.ts" --include="*.tsx" 2>/dev/null || true`,
      { encoding: 'utf8', stdio: ['ignore', 'pipe', 'ignore'] }
    );
  } catch {
    // grep exit 1 = no matches; that's fine
  }

  const TEST_PATHS = ['__fixtures__', '__tests__', '__mocks__', '.test.', '.spec.'];

  for (const rawLine of grepOutput.split('\n').filter(Boolean)) {
    const filePath = rawLine.split(':')[0];
    if (!filePath) continue;

    // Skip test/fixture paths
    if (TEST_PATHS.some(p => filePath.includes(p))) continue;

    // Skip Tier A allowed dirs
    const isInAllowedDir = allowedDirs.some(dir => filePath.includes(dir));
    if (isInAllowedDir) continue;

    violations.push({
      type: 'direct_anthropic_import',
      file: filePath,
      line: rawLine,
      message: `Direct @anthropic-ai/sdk import outside governed Tier A dirs: ${filePath}`,
    });
  }

  return violations;
}

/**
 * Scan `rootDir` recursively for files matching the `governed*Call` pattern
 * that are NOT registered in WRAPPER_REGISTRY.
 *
 * Registered Tier B routing shims are EXEMPT — they match the pattern but
 * are known, intentional, and documented. Only UNREGISTERED files are flagged.
 *
 * @param {string} rootDir - root directory to scan (relative to cwd)
 * @returns {Array<{ type: string, file: string, candidates: string[] }>}
 */
function scanUnregisteredGovernedCallFiles(rootDir) {
  if (!IS_NODE) return [];
  const { execSync } = await_require_sync('node:child_process');
  if (!execSync) return [];

  let findOutput = '';
  try {
    findOutput = execSync(
      `find "${rootDir}" -name "governed*Call*" -type f 2>/dev/null || true`,
      { encoding: 'utf8', stdio: ['ignore', 'pipe', 'ignore'] }
    );
  } catch {
    // find failure is non-fatal
  }

  const violations = [];
  const registeredPaths = new Set(ALL_REGISTERED_PATHS);

  for (const rawPath of findOutput.split('\n').filter(Boolean)) {
    // Normalize: strip leading './' if present
    const normalizedPath = rawPath.replace(/^\.\//, '');

    // Check if this path (or a suffix of it) matches any registered path
    const isRegistered = [...registeredPaths].some(
      rp => normalizedPath === rp || normalizedPath.endsWith(rp) || rp.endsWith(normalizedPath)
    );

    if (!isRegistered) {
      violations.push({
        type: 'unregistered_governed_call',
        file: normalizedPath,
        message: `Unregistered governed*Call file found: ${normalizedPath}. ` +
          'This file matches the governed*Call naming convention but is not in WRAPPER_REGISTRY. ' +
          'Either register it (with explicit tier declaration) or rename it. ' +
          'See docs/governance/wrapper-extension-protocol.md.',
      });
    }
  }

  return violations;
}

/**
 * Lazy-require helper to keep this module importable in browser/test contexts
 * that don't have Node built-ins. Returns null if unavailable.
 */
function await_require_sync(moduleName) {
  try {
    // Dynamic require for Node.js built-ins; unavailable in browser
    // eslint-disable-next-line no-undef
    return require(moduleName);
  } catch {
    return null;
  }
}

// ─── Main enforcement entry point ─────────────────────────────────────────────

/**
 * Full wrapper discipline enforcement.
 * In Node.js: registry check + direct-import scan + unregistered-file scan.
 * In browser: registry check only.
 *
 * @param {{ rootDir?: string, strict?: boolean }} options
 * @returns {{ violations: Array<object>, passed: boolean }}
 */
export function enforceAtStartup({ rootDir = 'src', strict = true } = {}) {
  const violations = [];

  // 1. Registry integrity (browser-safe)
  const registryCheck = checkRegistryIntegrity();
  violations.push(...registryCheck.violations);

  if (IS_NODE) {
    // 2. Direct Anthropic SDK imports outside Tier A dirs
    const tierADirs = Object.values(TIER_A_WRAPPERS).map(w => {
      const parts = w.path.split('/');
      parts.pop(); // remove filename
      return parts.join('/');
    });
    const importViolations = scanDirectAnthropicImports(rootDir, tierADirs);
    violations.push(...importViolations);

    // 3. Unregistered governed*Call files (4th-wrapper detection)
    // Note: only UNREGISTERED files flagged; Tier B shims are exempt
    const unregisteredViolations = scanUnregisteredGovernedCallFiles(rootDir);
    violations.push(...unregisteredViolations);
  }

  const result = { violations, passed: violations.length === 0 };

  if (strict && !result.passed) {
    console.error(
      '[WrapperDisciplineEnforcer] VIOLATIONS:\n' +
      JSON.stringify(violations, null, 2)
    );
    throw new Error(
      `[WrapperDisciplineEnforcer] Wrapper discipline violations: ${violations.length}. ` +
      'See docs/governance/wrapper-discipline.md'
    );
  }

  return result;
}

export default enforceAtStartup;
